import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { Icon, Tooltip, Typography } from '@mui/material'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppButton } from 'src/component/common/analog/molecule/button/AppButton'
import { AppSkeleton } from 'src/component/common/analog/molecule/AppSkelton'
import { Caption } from 'src/feature/localization/component/atom/Caption'
import { Word } from 'src/feature/localization/component/atom/Word'
import { useBool } from '../../../../hook/bool/useBool'
import { CreateCertificationDto } from '../../model/certification'
import { useCertificationStore } from '../../store/certification.store'
import { CertificationFormModal } from '../template/CertificationFormModal'
import { DeleteConfirmationModal } from '../template/DeleteConfirmationModal'
import { AppIconButton } from 'src/component/common/analog/molecule/button/AppIconButton'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import { iconMapping } from 'src/util/icon-mapping'
import { ColorTokens, Palette } from 'src/feature/theme/model/Palette'

export type CertificationsCardProps = {
  userId?: string
  initialOpenForm?: boolean
}

export const CertificationsCard = observer(({ userId, initialOpenForm }: CertificationsCardProps) => {
  const { translation } = useLocalizationStore()
  const certificationStore = useCertificationStore()
  const formModalBool = useBool(initialOpenForm)
  const deleteModalBool = useBool()
  const [selectedCertificationId, setSelectedCertificationId] = useState<string | null>(null)

  useEffect(() => {
    certificationStore.reset()
    certificationStore.handleGetCertifications(userId)
  }, [certificationStore, userId])

  const { isLoading } = certificationStore.getGenericFieldsState('handleGetCertifications')
  const { isLoading: isCreating } = certificationStore.getGenericFieldsState('handleCreateCertification')
  const { isLoading: isDeleting } = certificationStore.getGenericFieldsState('handleDeleteCertification')

  const isOperationInProgress = isCreating || isDeleting

  const handleSubmitCertification = async (data: CreateCertificationDto) => {
    await certificationStore.handleCreateCertification(data)
    formModalBool.makeFalse()
  }

  const handleDeleteCertification = (certificationId: string) => {
    setSelectedCertificationId(certificationId)
    deleteModalBool.makeTrue()
  }

  const handleConfirmDelete = async () => {
    if (selectedCertificationId) {
      await certificationStore.handleDeleteCertification(selectedCertificationId)
      setSelectedCertificationId(null)
    }
  }
  const pageTranslation = translation.pages.myTeam

  return (
    <>
      <AppPaper sx={styles.root}>
        <AppBox width='100%' justifyContent='space-between'>
          <AppBox alignItems='center' gap={5}>
            <Tooltip title={pageTranslation.licenseDesc()}
              placement='top' arrow>
              <Icon sx={{ height: 17, width: 17, color: 'secondary.main' }} component={iconMapping['InfoIcon']} />
            </Tooltip>
            <Typography variant='h5'>{translation.pages.profileInfo.careerGoalsCard.certifications()}</Typography>
            <Word sx={styles.pill}>{certificationStore.certificationsCount || '0'}</Word>
          </AppBox>
          {!userId && <AppButton
            fontSize={12}
            textValue={translation.buttons.add()}
            variant="light"
            onClick={formModalBool.makeTrue}
            disabled={isOperationInProgress}
          />}
        </AppBox>
        {isLoading && !certificationStore.certifications ? (
          <AppBox sx={styles.certificationsContainer}>
            <AppSkeleton
              count={3}
              direction="column"
              rects={[
                { width: '30%', height: 16 },
                { width: '50%', height: 14 },
                { width: '70%', height: 12 },
              ]}
            />
          </AppBox>
        ) : certificationStore.hasCertifications ? (
          <AppBox sx={styles.certificationsContainer}>
            {certificationStore.certifications!.map(certification => (
              <AppBox key={certification.id} sx={styles.certificationItem}>
                <AppBox sx={styles.certificationContent}>
                  <Typography sx={styles.certificationName}>
                    {certification.certificateName}
                  </Typography>
                  {certification.issuer && (
                    <Typography sx={styles.certificationIssuer}>
                      {certification.issuer}
                    </Typography>
                  )}
                  <AppBox sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'primary.main',
                        fontSize: 10,
                      }}
                    >
                      {new Date(certification.dateObtained).toLocaleDateString()}
                    </Typography>
                    {certification.expiryDate && (() => {
                      const isExpired = new Date(certification.expiryDate) < new Date()
                      
                      return (
                        <>
                          <Typography sx={{ color: 'text.secondary', fontSize: '10px' }}>
                            • {isExpired ? translation.pages.profile.certificationModal.expired() : translation.pages.profile.certificationModal.expires()}:
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: isExpired ? 'error.main' : Palette.GOLDEN_ORANGE,
                              fontSize: 10,
                              fontWeight: isExpired ? 600 : 400,
                            }}
                          >
                            {new Date(certification.expiryDate).toLocaleDateString()}
                            {isExpired && ' ⚠️'}
                          </Typography>
                        </>
                      )
                    })()}
                  </AppBox>
                </AppBox>
                <AppBox sx={styles.certificationActions}>
                  {!userId && <AppIconButton
                    size='small'
                    sx={styles.iconButton}
                    onClick={() => handleDeleteCertification(certification.id)}
                    disabled={isDeleting}
                  >
                    <DeleteOutlineRoundedIcon
                      fontSize='small'
                      sx={{
                        transition: 'color 0.3s',
                        '&:hover': {
                          color: 'error.main',
                        },
                      }}
                    />
                  </AppIconButton>}
                </AppBox>
              </AppBox>
            ))}
          </AppBox>
        ) : (
          <Caption sx={{ color: 'text.secondary', fontSize: 11 }}>
            {translation.noData()}
          </Caption>
        )}
      </AppPaper>

      <CertificationFormModal
        {...formModalBool}
        onSubmit={handleSubmitCertification}
      />

      <DeleteConfirmationModal
        {...deleteModalBool}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
      />
    </>
  )
})

const styles = {
  root: {
    padding: '10px 15px',
    borderRadius: '10px',
    gap: 10,
    width: '100%',
    border: 0,
  },
  pill: {
    width: 25,
    height: 25,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '999px',
    backgroundColor: ColorTokens.primary10,
    color: ColorTokens.primary500,
  },
  certificationsContainer: {
    flexDirection: 'column',
    gap: 10,
    width: '100%',
    height: 270,
    overflow: 'auto',
  },
  certificationItem: {
    padding: 10,
    borderRadius: '10px',
    backgroundColor: 'background.dark',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 10,
  },
  certificationContent: {
    flexDirection: 'column',
    gap: 5,
    flex: 1,
  },
  certificationName: {
    fontWeight: 600,
    color: 'text.primary',
    fontSize: 12,
  },
  certificationIssuer: {
    color: 'text.secondary',
    fontSize: 11,
  },
  certificationActions: {
    gap: 8,
    flexShrink: 0,
  },
  iconButton: {
    border: '1px solid',
    borderRadius: '50%',
    borderColor: 'text.secondary',
    color: 'text.primary',
    backgroundColor: 'background.paper',
    transition: 'transform 0.3s',
    '&:hover': {
      transform: 'scale(1.05)',
    },
  },
}
